import { MeshBuilder, Vector3, type Mesh } from "@babylonjs/core";
import { HeroId, Slot, ck, fxk, hv, zk } from "@rivalwatch/shared";
import { baked } from "../../audio/bake.ts";
import { bell, click, clinks, drive, fm, later, mallet, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import * as loops from "../../audio/cues/loops.ts";
import { glow, toon } from "../../game/materials.ts";
import type { ClientHero } from "../types.ts";

const CK_DOUBLE = ck(HeroId.Lowkey, 0);
const CK_DECOY = ck(HeroId.Lowkey, 1);
const ZK_RUNE = zk(HeroId.Lowkey, 0);
const FX_TRICK = fxk(HeroId.Lowkey, 0);
const hz = (semi: number) => 293.66 * 2 ** (semi / 12);
const theme = {
  bpm: 118,
  octave: 0,
  voice: mallet,
  notes: [
    [0, 0.5],
    [6, 0.25],
    [7, 0.25],
    [4, 0.5],
    [12, 0.75],
  ],
} satisfies ClientHero["audio"]["theme"];

const scepterCue = baked("fire:lowkey:scepter", () =>
  trim(15.6, (o) => {
    click(o, 0.18, 3600, 0.004);
    fm(o, { f: hz(16), ratio: 1.67, index: 3, index1: 0.2, gain: 0.08, d: 0.16 });
    noise(o, { color: "crackle", type: "bandpass", f: 2100, q: 1, gain: 0.16, d: 0.12 });
    return 0.32;
  }), { variants: 5, jitter: 0.015 });

const curtainCue = baked("ability:lowkey:curtain", () =>
  trim(14.6, (o) => {
    whoosh(o, 0.38, 400, 2600, 0.32);
    clinks(o, 4, 0.06, 0.22, 900, 2400);
    tone(later(o, 0.08), { type: "triangle", f: hz(6), f1: hz(18), glide: 0.28, gain: 0.065, d: 0.32 });
    return 0.75;
  }), { variants: 3, jitter: 0.01 });

const runeCue = baked("ability:lowkey:rune", () =>
  trim(9.8, (o) => {
    bell(o, hz(12), 0.35, 0.65);
    bell(later(o, 0.08), hz(18), 0.35, 0.55);
    noise(o, { color: "pink", type: "bandpass", f: 700, f1: 1800, q: 1.6, gain: 0.28, a: 0.08, d: 0.4 });
    return 0.9;
  }), { variants: 2, jitter: 0.005 });

const doubleCue = baked("ability:lowkey:double", () =>
  trim(13.8, (o) => {
    click(o, 0.18, 1800, 0.01);
    tone(drive(o, 1.8), { type: "sawtooth", f: hz(-5), f1: hz(7), glide: 0.22, gain: 0.08, d: 0.25, lp: 1300 });
    whoosh(later(o, 0.08), 0.32, 700, 2200, 0.22);
    return 0.55;
  }), { variants: 3, jitter: 0.01 });

const swapCue = baked("ability:lowkey:swap", () =>
  trim(5.3, (o) => {
    thump(o, 0.42, 120, 48, 0.18);
    fm(o, { f: hz(11), ratio: 2.5, index: 3.2, index1: 0, gain: 0.09, d: 0.18 });
    noise(o, { color: "white", type: "highpass", f: 3200, gain: 0.16, d: 0.1 });
    return 0.42;
  }), { variants: 3, jitter: 0.01 });

const crownCue = baked("ability:lowkey:crown", () =>
  trim(0, (o) => {
    thump(o, 0.55, 84, 30, 0.35);
    bell(o, hz(18), 0.5, 0.7);
    bell(later(o, 0.08), hz(30), 0.55, 0.6);
    tone(drive(o, 2.1), { type: "sawtooth", f: hz(-12), f1: hz(0), glide: 0.5, gain: 0.08, a: 0.08, d: 0.5, lp: 1100 });
    return 1.1;
  }), { variants: 2, jitter: 0.005 });

const lowkeyReload = (seconds: number) => baked("reload:lowkey:scepter", () =>
  trim(14.8, (o) => {
    clinks(o, 3, 0.04, 0.2, 1300, 2600);
    tone(later(o, seconds * 0.45), { type: "triangle", f: hz(11), f1: hz(23), glide: seconds * 0.3, gain: 0.035, d: seconds * 0.4 });
    click(o, 0.12, 3200, 0.006, seconds * 0.82);
    return seconds + 0.2;
  }), { variants: 2, jitter: 0.01 });

type LowkeyGear = { staff: Mesh; gem: Mesh; cloak: Mesh; tineL: Mesh; tineR: Mesh };

const hero: ClientHero<LowkeyGear> = {
  id: HeroId.Lowkey,
  bulk: 0.98,
  shape: { head: 0.95, torso: 1, arms: 1, legs: 1.05 },
  gear(g) {
    const { scene, rig, add, glow, toon, R, H } = g;
    const staff = add(MeshBuilder.CreateCylinder("forked-staff", { diameter: 0.07, height: H * 0.68, tessellation: 8 }, scene), rig.handR, toon("#f5d76e"));
    staff.rotation.x = Math.PI / 2;
    staff.position.z = -H * 0.28;
    const tineL = add(MeshBuilder.CreateCylinder("staff-tine-l", { diameterTop: 0.02, diameterBottom: 0.045, height: 0.28, tessellation: 8 }, scene), rig.handR, toon("#f5d76e"));
    const tineR = add(MeshBuilder.CreateCylinder("staff-tine-r", { diameterTop: 0.02, diameterBottom: 0.045, height: 0.28, tessellation: 8 }, scene), rig.handR, toon("#f5d76e"));
    tineL.position.set(-0.08, 0.02, -H * 0.64);
    tineR.position.set(0.08, 0.02, -H * 0.64);
    tineL.rotation.x = tineR.rotation.x = Math.PI / 2;
    tineL.rotation.z = -0.35;
    tineR.rotation.z = 0.35;
    const gem = add(MeshBuilder.CreateSphere("staff-gem", { diameter: 0.18, segments: 10 }, scene), rig.handR, glow("#3fae5c"));
    gem.position.z = -H * 0.62;
    for (const side of [-1, 1]) {
      const horn = add(MeshBuilder.CreateCylinder("low-horn", { diameterTop: 0.02, diameterBottom: 0.08, height: 0.42, tessellation: 8 }, scene), rig.head, toon("#f5d76e"));
      horn.position.set(side * R * 0.45, 0.28, 0);
      horn.rotation.z = side * 0.55;
    }
    const cloak = add(MeshBuilder.CreateBox("trick-cloak", { width: R * 1.55, height: H * 0.42, depth: 0.05 }, scene), rig.torso, toon("#08180e"));
    cloak.position.set(0, -0.22, R * 0.88);
    return { staff, gem, cloak, tineL, tineR };
  },
  animate(gear, f, rig) {
    const hidden = f.v[0] > 0.01;
    const copied = f.v[2] > 0;
    const ult = f.ultActive || f.v[3] > 0.01 || copied;
    for (const p of Object.values(rig.parts)) if (p) p.visibility = hidden ? 0.4 : 1;
    gear.gem.scaling.setAll(1 + Math.sin(f.t * 8) * 0.12 + (ult ? 0.45 : 0));
    gear.cloak.rotation.x = Math.sin(f.t * 2) * 0.08;
    gear.staff.rotation.z = Math.sin(f.t * 4) * (ult ? 0.12 : 0.04);
    if (ult) return "holding-both";
    if (f.action === 1) return "holding-right-shoot";
    if (f.action === 4) return "interact-right";
    return "holding-right";
  },
  hud(c, eid) {
    return [
      { label: "Illusions", value: hv(c, eid, 1) / 2, color: "#3fae5c" },
      { label: "Copied target", value: hv(c, eid, 2) > 0 ? 1 : 0, color: "#f5d76e" },
    ];
  },
  constructs: {
    [CK_DOUBLE]: (scene) => {
      const body = MeshBuilder.CreateBox("lowkey-double-body", { width: 0.72, height: 1.0, depth: 0.42 }, scene);
      body.material = toon(scene, "#163822");
      const head = MeshBuilder.CreateBox("lowkey-double-head", { width: 0.44, height: 0.44, depth: 0.44 }, scene);
      head.material = toon(scene, "#d39a70");
      const cape = MeshBuilder.CreateBox("lowkey-double-cape", { width: 0.78, height: 0.9, depth: 0.04 }, scene);
      cape.material = toon(scene, "#08180e");
      const hornL = MeshBuilder.CreateCylinder("lowkey-double-horn-l", { diameterTop: 0.02, diameterBottom: 0.06, height: 0.34, tessellation: 8 }, scene);
      const hornR = MeshBuilder.CreateCylinder("lowkey-double-horn-r", { diameterTop: 0.02, diameterBottom: 0.06, height: 0.34, tessellation: 8 }, scene);
      hornL.material = hornR.material = toon(scene, "#f5d76e");
      return {
        gear: { body, head, cape, hornL, hornR },
        update(_g, f) {
          body.position.set(f.pos.x, f.pos.y + 0.93, f.pos.z);
          head.position.set(f.pos.x, f.pos.y + 1.62, f.pos.z - 0.02);
          cape.position.set(f.pos.x, f.pos.y + 0.95, f.pos.z + 0.27);
          hornL.position.set(f.pos.x - 0.16, f.pos.y + 1.95, f.pos.z);
          hornR.position.set(f.pos.x + 0.16, f.pos.y + 1.95, f.pos.z);
          body.rotation.y = f.yaw;
          head.rotation.y = cape.rotation.y = hornL.rotation.y = hornR.rotation.y = f.yaw;
          hornL.rotation.z = -0.5;
          hornR.rotation.z = 0.5;
          for (const m of [body, head, cape, hornL, hornR]) m.visibility = 0.72;
        },
        dispose: () => [body, head, cape, hornL, hornR].forEach((m) => m.dispose()),
      };
    },
    [CK_DECOY]: (scene) => {
      const body = MeshBuilder.CreateBox("lowkey-decoy-body", { width: 0.72, height: 1.0, depth: 0.42 }, scene);
      body.material = glow(scene, "#3fae5c", 0.45);
      const head = MeshBuilder.CreateBox("lowkey-decoy-head", { width: 0.44, height: 0.44, depth: 0.44 }, scene);
      head.material = glow(scene, "#f5d76e", 0.45);
      return {
        gear: { body, head },
        update(_g, f) {
          body.position.set(f.pos.x, f.pos.y + 0.93, f.pos.z);
          head.position.set(f.pos.x, f.pos.y + 1.62, f.pos.z);
          body.rotation.y = f.yaw;
          head.rotation.y = f.yaw;
          for (const m of [body, head]) m.visibility = 0.45 + Math.sin(f.ticks * 0.08) * 0.12;
        },
        dispose: () => [body, head].forEach((m) => m.dispose()),
      };
    },
  },
  zones: {
    [ZK_RUNE]: (scene, f) => {
      const ring = MeshBuilder.CreateTorus("mercy-rune", { diameter: f.radius * 2, thickness: 0.04, tessellation: 40 }, scene);
      ring.material = glow(scene, "#3fae5c", 0.35);
      ring.rotation.x = Math.PI / 2;
      return {
        gear: ring,
        update(_m, fr) {
          ring.position.set(fr.pos.x, fr.pos.y + 0.05, fr.pos.z);
          ring.rotation.z += fr.dt;
          ring.scaling.setAll(1 + Math.sin(fr.age * 6) * 0.03);
        },
        dispose: () => ring.dispose(),
      };
    },
  },
  fx: {
    [FX_TRICK]: {
      impact(c, pos) {
        const ring = MeshBuilder.CreateTorus("lowkey-swap-ring", { diameter: 1.8, thickness: 0.05, tessellation: 36 }, c.scene);
        ring.position.copyFrom(new Vector3(pos.x, pos.y + 0.08, pos.z));
        ring.rotation.x = Math.PI / 2;
        ring.material = c.glow("#3fae5c", 0.75);
        c.timed(ring, 0.45, (t, m) => {
          m.scaling.setAll(0.4 + t * 1.2);
          m.rotation.z = t * Math.PI * 2;
          m.visibility = 1 - t;
        });
      },
    },
  },
  audio: {
    weight: "mid",
    fire: () => ({ key: "fire:lowkey:scepter", cue: scepterCue }),
    reload: lowkeyReload,
    ability: {
      [Slot.Secondary]: curtainCue,
      [Slot.Ability1]: runeCue,
      [Slot.Ability2]: doubleCue,
      [Slot.Ability3]: swapCue,
      [Slot.Ultimate]: crownCue,
    },
    theme,
    themeTrim: { heroic: 2.3, hostile: -6.1, tease: 4.5 },
    zoneLoops: { [ZK_RUNE]: { build: loops.beacon, gain: 0.45, range: 28 } },
    constructLoops: { [CK_DOUBLE]: { build: loops.beacon, gain: 0.22, range: 24 }, [CK_DECOY]: { build: loops.beacon, gain: 0.16, range: 18 } },
  },
  icons: {
    [Slot.Primary]: "M12 3 V20 M8 6 H16 M10 3 L7 7 M14 3 L17 7",
    [Slot.Secondary]: "M5 18 C8 10 16 10 19 18 M8 18 C10 15 14 15 16 18",
    [Slot.Ability1]: "M12 4 L19 8 V16 L12 20 L5 16 V8 Z M8 12 H16",
    [Slot.Ability2]: "M7 7 H17 V17 H7 Z M10 10 H20 V20 H10",
    [Slot.Ability3]: "M6 8 H18 M18 8 L14 4 M18 8 L14 12 M18 16 H6 M6 16 L10 12 M6 16 L10 20",
    [Slot.Ultimate]: "M5 9 L9 14 L12 5 L15 14 L19 9 L17 20 H7 Z",
    passive: "M8 17 C10 20 14 20 16 17 M9 12 C10 9 14 9 15 12",
  },
};

export default hero;
